import React, {useState, useContext, useEffect, useMemo, useRef} from 'react'
import {useTranslation} from 'react-i18next'
import {formatDateLegend, getTimeZoneNotice} from '@common/utils/dates'
import {
  currentYyMm,
  getCalendarMatrix,
  buildSlotsByDate,
  yymmToYearMonth,
} from '@common/utils'

import {
  Grid,
  Column,
  RescheduleFormCard,
  OrangeButton,
  SelectedGlobal,
} from '../bookings/AppointmentManagementPage.styled'
import {CalendarPanel} from '../bookings/CalendarPanel'
import {DayTimePanel} from '../bookings/DayTimePanel'

import {Typography} from '~/components/general/typography/Typography'
import {ProcessingOverlay} from '~/components/general/processingOverlay/ProcessingOverlay'
import {useMonthNavigation} from '~/hooks/useMonthNavigation'
import {useWeekdayShorts} from '~/hooks/useWeekdayShorts'
import {
  NotificationContext,
  NotificationType,
} from '~/components/notifications/NotificationContext'
import {
  useReschedulePartnerServiceSlot,
  useGetPartnerServiceAvailabilities,
} from '~/api/partnerAdminSlots'
import {Container} from '~/components/general/container/Container'
import {TextArea} from '~/components/general/textArea/TextArea'
import {
  ModalBody,
  ModalHeader,
  ModalContent,
  StyledModal,
} from '~/components/general/modal/SideModal.styled'
import {Portal} from '~/components/general/modal/Portal'
import {Icon} from '~/components/general/icon/Icon'

type AvailabilityDay = {date: string; slots: string[]}

type RescheduleAppointmentModalProps = {
  isVisible: boolean
  onClose: () => void
  slotId: string
  partnerServiceId: string
  onSuccess: () => void
}

export const RescheduleAppointmentModal = ({
  isVisible,
  onClose,
  slotId,
  partnerServiceId,
  onSuccess,
}: RescheduleAppointmentModalProps) => {
  const {t} = useTranslation()
  const {addNotification} = useContext(NotificationContext)
  const [comment, setComment] = useState('')

  // Reschedule UI state
  const [apiMonthSpec, setApiMonthSpec] = useState<string>('FIRST')
  const [calendarYyMm, setCalendarYyMm] = useState<string>(currentYyMm())
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [selectedTime, setSelectedTime] = useState<string | null>(null)

  const rescheduleMutation = useReschedulePartnerServiceSlot(
    partnerServiceId,
    slotId,
  )

  const {
    data: avail,
    isPending,
    isFetching,
  } = useGetPartnerServiceAvailabilities(partnerServiceId, apiMonthSpec, {
    enabled: isVisible && !!partnerServiceId,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  })

  const days = useMemo<AvailabilityDay[]>(
    () => (avail?.days as AvailabilityDay[]) ?? [],
    [avail?.days],
  )

  // Set visible calendar month based on API response ONCE per open
  const didSetCalendarFromApiRef = useRef(false)

  // Ensure each time we open reschedule, we start with FIRST and reset calendar sync
  useEffect(() => {
    if (!isVisible) return
    setApiMonthSpec('FIRST')
    setCalendarYyMm(currentYyMm())
    didSetCalendarFromApiRef.current = false
    setSelectedDate(null)
    setSelectedTime(null)
    setComment('')
  }, [isVisible])

  useEffect(() => {
    if (!avail || !isVisible) return
    if (didSetCalendarFromApiRef.current) return
    const yymm = avail?.yearMonth
    if (yymm) setCalendarYyMm(yymm)
    didSetCalendarFromApiRef.current = true
  }, [avail, isVisible])

  // Calendar derived data
  const cells = useMemo(() => getCalendarMatrix(calendarYyMm), [calendarYyMm])

  const selectedDateLegend = useMemo(
    () => formatDateLegend(selectedDate || undefined),
    [selectedDate],
  )

  // Build slots map from API days
  const slotsByDate = useMemo(() => buildSlotsByDate(days), [days])

  // Initialize selected date from first available day in current month
  useEffect(() => {
    if (!isVisible) return
    const first = days.find((d: AvailabilityDay) => (d.slots?.length || 0) > 0)
    if (first) {
      setSelectedDate(first.date)
    } else if (avail) {
      const {year, month} = yymmToYearMonth(calendarYyMm)
      const firstDayOfMonth = `${year}/${String(month).padStart(2, '0')}/01`
      setSelectedDate(firstDayOfMonth)
    }
    setSelectedTime(null)
  }, [isVisible, days, avail, calendarYyMm])

  const weekdayShorts = useWeekdayShorts()

  const {gotoPrevMonth, gotoNextMonth, monthLabel, disablePrev} =
    useMonthNavigation({
      currentYyMm: calendarYyMm,
      onMonthChange: (yyMm) => {
        setCalendarYyMm(yyMm)
        setApiMonthSpec(yyMm)
      },
    })

  const handleConfirm = () => {
    if (!selectedTime || !selectedDate) return

    rescheduleMutation.mutate(
      {
        urlParams: {
          partnerServiceId,
          fromPartnerServiceSlotId: slotId,
        },
        requestBody: {
          date: selectedDate,
          time: selectedTime,
          comment: comment.trim() || null,
        },
      },
      {
        onSuccess: () => {
          addNotification(
            t('Appointment rescheduled successfully'),
            NotificationType.Success,
          )
          setComment('')
          setSelectedDate(null)
          setSelectedTime(null)
          onSuccess()
        },
        onError: (error: Error) => {
          addNotification(error.message, NotificationType.Error)
        },
      },
    )
  }

  const handleCancel = () => {
    setComment('')
    setSelectedDate(null)
    setSelectedTime(null)
    onClose()
  }

  useEffect(() => {
    const handleEscapeClose = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    if (isVisible) {
      document.addEventListener('keydown', handleEscapeClose)
      return () => {
        document.removeEventListener('keydown', handleEscapeClose)
      }
    }
  }, [isVisible, onClose])

  const timeZoneNotice = getTimeZoneNotice(undefined, t)
  const noAvailabilityText = t(
    'There is no availability on this date. Please choose another one.',
  )

  if (!isVisible) return null

  const showProcessingOverlay =
    isFetching || isPending || rescheduleMutation.isPending

  return (
    <Portal selector="#modal">
      <ModalBody onClick={handleCancel}>
        <ProcessingOverlay isVisible={showProcessingOverlay} />
        <StyledModal modalSize="wide" onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <Typography size="lg" weight="medium">
              {t('Reschedule Appointment')}
            </Typography>
            <Icon onClick={handleCancel} circled="md" name="Close" size="sm" />
          </ModalHeader>
          <ModalContent>
            <Container direction="column" spacing={2}>
              {selectedDate && (
                <SelectedGlobal>
                  <Typography size="xl" weight="bold">
                    {selectedDateLegend}
                  </Typography>
                </SelectedGlobal>
              )}
              <Grid>
                <Column>
                  <CalendarPanel
                    dateTitle={t('DATE')}
                    monthLabel={monthLabel}
                    weekdayShorts={weekdayShorts}
                    cells={cells}
                    slotsByDate={slotsByDate}
                    selectedDate={selectedDate}
                    onSelectDate={(dateKey) => {
                      setSelectedDate(dateKey)
                      setSelectedTime(null)
                    }}
                    disablePrev={disablePrev}
                    onPrevMonth={gotoPrevMonth}
                    onNextMonth={gotoNextMonth}
                  />
                </Column>
                <Column>
                  <DayTimePanel
                    title={t('TIME')}
                    selectedDate={selectedDate}
                    selectedDateSlots={
                      selectedDate ? slotsByDate.get(selectedDate) || [] : []
                    }
                    selectedTime={selectedTime}
                    onSelectTime={(time) => setSelectedTime(time)}
                    isLoading={rescheduleMutation.isPending}
                    timeZoneNotice={timeZoneNotice}
                    noAvailabilityText={noAvailabilityText}
                  />
                </Column>
              </Grid>
              <RescheduleFormCard>
                <Container direction="column" spacing={2}>
                  <Typography weight="bold">{t('Add a comment')}</Typography>
                  <TextArea
                    height="120px"
                    placeholder={t(
                      'Write your reason for rescheduling (optional)',
                    )}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                  <Typography color="warning" fontStyle="italic">
                    {t('This comment will be visible to the patient')}
                  </Typography>
                  <Container
                    flex
                    direction="row"
                    justify="flex-end"
                    spacing={1}>
                    <OrangeButton color="secondary" onClick={handleCancel}>
                      {t('Back')}
                    </OrangeButton>
                    <OrangeButton
                      disabled={!selectedTime || rescheduleMutation.isPending}
                      onClick={handleConfirm}>
                      {t('Confirm')}
                    </OrangeButton>
                  </Container>
                </Container>
              </RescheduleFormCard>
            </Container>
          </ModalContent>
        </StyledModal>
      </ModalBody>
    </Portal>
  )
}
